import React, { forwardRef, useEffect, useState } from "react";
import chevronRightIcon from "../../assets/images/chevron-right.svg";
import { HeaderTheme } from "./HeaderTheme";
import { NavigationItem } from "./NavigationItem";
import { NavigationNestedSection } from "./NavigationNestedSection";

interface Props {
  items: NavigationItem[];
  selectedTopLevel?: number | null;
  open?: boolean;
  color?: string;
  isMobile?: boolean;
  updateContainerHeight: (height?: number) => void;
}

const theme = {
  [HeaderTheme.Primary]: {
    section: "navigation-section--primary",
    item: "navigation-section-item--primary"
  },
  [HeaderTheme.Secondary]: {
    section: "navigation-section--secondary",
    item: "navigation-section-item--secondary"
  }
};

export const NavigationTopSection = forwardRef(
  (props: Props, ref: React.RefObject<HTMLUListElement>) => {
    const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);

    useEffect(() => {
      // HACK: reset selected item with a delay
      // to ensure new items are properly listed
      // when changing top level navigation and pre-selecting
      // the first item in the top section

      setSelectedItemIndex(null);

      if (props.open) {
        if (!props.isMobile) {
          setTimeout(() => setSelectedItemIndex(0), 10);
        }
      }
    }, [props.open, props.isMobile, props.selectedTopLevel]);

    const onSubLevelExpanded = (e: React.MouseEvent<HTMLElement>, index: number) => {
      setSelectedItemIndex(index);
      e.currentTarget.blur();
    };

    const resetSelection = (e: React.MouseEvent<HTMLElement>) => {
      setSelectedItemIndex(null);
      e.currentTarget.blur();

      // reset height after closing sublevel
      props.updateContainerHeight?.();
    };

    const currentTheme = theme[props.color ?? HeaderTheme.Primary];

    if (!props.items?.length) {
      return null;
    }

    return (
      <div
        className={`navigation-section ${currentTheme.section} ${
          props.open
            ? selectedItemIndex !== null
              ? "navigation-section--nested-active"
              : "navigation-section--active"
            : "navigation-section--inactive"
        }`}
      >
        <ul ref={ref} className="navigation-section-inner">
          {props.items.map((item, index) => (
            <li key={index} className="flex w-full">
              <button
                type="button"
                className={`navigation-section-item ${currentTheme.item} last:mb-0 ${
                  selectedItemIndex === index && "navigation-section-item--active"
                }`}
                onClick={e => onSubLevelExpanded(e, index)}
              >
                <span className="mr-5 text-left">{item.title}</span>
                <svg
                  width={16}
                  height={16}
                  className="fill-current w-2 h-3 xl:w-auto xl:h-auto"
                >
                  <use xlinkHref={chevronRightIcon.url} />
                </svg>
              </button>
              {!!item.items?.length && (
                <NavigationNestedSection
                  items={item.items}
                  open={selectedItemIndex === index}
                  backButtonLabel={item.title}
                  color={props.color ?? ""}
                  onClose={resetSelection}
                  updateContainerHeight={props.updateContainerHeight}
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }
);

